import React, { useState } from 'react';
import { LockIcon, ScaleIcon, InfoIcon, CheckIcon, StickyNoteIcon, XIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { copy } from '../copy';
import { ConfidenceBand } from '../components/discovery/ConfidenceBand';
import { Provenance } from '../components/discovery/Provenance';
type Disposition = 'accept' | 'note' | 'dismiss' | null;
export function SentencingPage() {
  const s = copy.sentencing;
  const [unlocked, setUnlocked] = useState(false);
  const [dispositions, setDispositions] = useState<Record<string, Disposition>>({});
  if (!unlocked) {
    return <div className="min-h-screen bg-white dark:bg-[#1a1a1a] text-slate-900 dark:text-[#e5e5e5] transition-colors duration-200" data-id="element-620">
        <Header title={s.headerTitle} minimal showBackArrow backTo="/case" data-id="element-621" />
        <main className="mx-auto max-w-2xl px-6 py-24 text-center" data-id="element-622">
          <div className="flex justify-center mb-6" data-id="element-623">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 dark:bg-[#222] border border-slate-200 dark:border-[#333]" data-id="element-624">
              <LockIcon size={28} className="text-slate-400 dark:text-[#555]" data-id="element-625" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2" data-id="element-626">
            {s.lockedTitle}
          </h1>
          <p className="text-slate-500 dark:text-[#a3a3a3] mb-8" data-id="element-627">
            {s.lockedBody}
          </p>
          <button onClick={() => setUnlocked(true)} className="px-4 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-medium hover:bg-slate-800 dark:hover:bg-slate-100 transition-all shadow-sm" data-id="element-628">
            {s.unlockButton}
          </button>
        </main>
      </div>;
  }
  const dispoButton = (id: string, kind: 'accept' | 'note' | 'dismiss', label: string, Icon: typeof CheckIcon, activeClass: string) => {
    const active = dispositions[id] === kind;
    return <button onClick={() => setDispositions(prev => ({
      ...prev,
      [id]: active ? null : kind
    }))} className={`flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-all ${active ? activeClass : 'border-slate-200 dark:border-[#333] text-slate-500 dark:text-[#a3a3a3] hover:bg-slate-50 dark:hover:bg-white/5'}`} data-id="element-629">
        <Icon size={13} data-id="element-630" />
        {label}
      </button>;
  };
  return <div className="min-h-screen bg-white dark:bg-[#1a1a1a] text-slate-900 dark:text-[#e5e5e5] transition-colors duration-200" data-id="element-631">
      <Header title={s.headerTitle} minimal showBackArrow backTo="/case" data-id="element-632" />

      <main className="mx-auto max-w-4xl px-6 py-12" data-id="element-633">
        <div className="mb-6" data-id="element-634">
          <h1 className="flex items-center gap-2 text-3xl font-bold text-slate-900 dark:text-white" data-id="element-635">
            <ScaleIcon size={26} className="text-blue-500" data-id="element-636" />
            {s.title}
          </h1>
          <p className="text-slate-500 dark:text-[#a3a3a3] mt-1" data-id="element-637">
            {s.subtitle}
          </p>
        </div>

        <div className="sticky top-16 z-10 mb-8 flex items-start gap-2 rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3.5 text-sm text-amber-800 dark:text-amber-300" data-id="element-638">
          <InfoIcon size={16} className="mt-0.5 shrink-0" data-id="element-639" />
          {s.banner}
        </div>

        <div className="space-y-8" data-id="element-640">
          {s.sections.map(section => <section key={section.id} className="space-y-3" data-id="element-641">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white border-b border-slate-100 dark:border-[#262626] pb-2" data-id="element-642">
                {section.label}
              </h2>
              {section.items.map(item => <motion.div key={item.id} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} className="rounded-2xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] p-4 space-y-3" data-id="element-643">
                  <p className="text-sm text-slate-700 dark:text-[#e5e5e5] leading-relaxed" data-id="element-644">
                    {item.observation}
                  </p>
                  <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-645">
                    <Provenance source={item.source} data-id="element-646" />
                    <ConfidenceBand value={item.confidence} compact data-id="element-647" />
                  </div>
                  <div className="flex items-center gap-2 pt-1" data-id="element-648">
                    {dispoButton(item.id, 'accept', s.dispositions.accept, CheckIcon, 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800')}
                    {dispoButton(item.id, 'note', s.dispositions.note, StickyNoteIcon, 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800')}
                    {dispoButton(item.id, 'dismiss', s.dispositions.dismiss, XIcon, 'bg-slate-100 dark:bg-[#2a2a2a] text-slate-600 dark:text-[#a3a3a3] border-slate-300 dark:border-[#444]')}
                  </div>
                  {dispositions[item.id] && <textarea rows={2} placeholder={s.annotationPlaceholder} className="w-full rounded-xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-2.5 text-sm text-slate-700 dark:text-[#e5e5e5] outline-none focus:ring-2 focus:ring-slate-200 dark:focus:ring-slate-800 resize-none" data-id="element-649" />}
                </motion.div>)}
            </section>)}
        </div>
      </main>
    </div>;
}